import { useMemo } from 'react';
import {
  useCurrentRefinements,
  useHits,
} from 'components/commercetools-ui/organisms/product/product-list-ssr/local-store';
import BooleanFacet from '../components/facets/boolean';
import ColorFacet from '../components/facets/color';
import CollectionTermFacet from '../components/facets/collection-term';
import RangeFacet from '../components/facets/range';
import TermFacet from '../components/facets/term';
import { FacetProps } from '../components/facets/types';
import { FacetConfiguration } from '../types';
import usePath from 'helpers/hooks/usePath';

interface Options {
  configuration?: Record<string, FacetConfiguration>;
  /**
   * Collection listings render every facet as the same pill row (CollectionTermFacet) regardless of
   * its configured type — that is the collection chrome, not a different data shape. The category
   * sidebar maps each type to its own control.
   */
  collectionMode?: boolean;
  render?: (result: { attribute: string; Component: JSX.Element }) => JSX.Element;
}

const CATEGORY_FACETS: Record<FacetConfiguration['type'], React.ComponentType<FacetProps>> = {
  range: RangeFacet,
  color: ColorFacet,
  term: TermFacet,
  boolean: BooleanFacet,
};

const COLLECTION_FACETS: Record<FacetConfiguration['type'], React.ComponentType<FacetProps>> = {
  range: CollectionTermFacet,
  color: CollectionTermFacet,
  term: CollectionTermFacet,
  boolean: CollectionTermFacet,
};

/**
 * Renders the configured facet panel.
 *
 * Facet ORDER is the configuration's own order — the Contentstack `facets_configuration` array. It is
 * no longer reordered by Algolia's `renderingContent.facetOrdering`: the server path never asks
 * Algolia for facets at all (plp-dataset-core), so that field is not fetched, not cached and not
 * shipped. A facet with no values in the current result set is hidden rather than dropped, so the
 * panel does not reflow as refinements change.
 */
const useDynamicFacets = ({ configuration, collectionMode = false, render }: Options = {}) => {
  const path = usePath();
  const { results } = useHits();
  const { items } = useCurrentRefinements();
  // Flexispray shows the first 8 facets until the visitor picks something, then all of them.
  const visibleItemsCount = items.length > 0 ? 9999 : 8;

  const facetMapping = collectionMode ? COLLECTION_FACETS : CATEGORY_FACETS;

  const dynamicFacets = useMemo(() => {
    if (!configuration) return <></>;

    const facets = Object.keys(configuration).map((attribute) => {
      const facet = configuration[attribute];
      const isEmptyFacet = !results?.disjunctiveFacets?.find((f) => f.name === attribute);
      const Component = facetMapping[facet.type];
      const FinalComponent = <Component key={attribute} label={facet.label} attribute={attribute} />;

      return {
        attribute,
        Component: (
          <div key={attribute} className={isEmptyFacet ? 'hidden' : 'block'}>
            {render?.({ attribute, Component: FinalComponent }) ?? FinalComponent}
          </div>
        ),
      };
    });

    return path.path.includes('flexispray')
      ? facets.slice(0, visibleItemsCount).map((facet) => facet?.Component)
      : facets.map((facet) => facet?.Component);
  }, [facetMapping, configuration, render, results?.disjunctiveFacets, path.path, visibleItemsCount]);

  return dynamicFacets;
};

export default useDynamicFacets;
